<template>
    <common-modal width="extra-large" @close="emitEvent({ name: 'close' })">
        <div v-if="showPricingDetails && !isPracticePremium" class="pricing-cards">
            <pricing-cards @upgrade="showPricingDetails = !showPricingDetails"></pricing-cards>
        </div>
        <div v-else class="billing-information">
            <div v-if="!isPracticePremium" class="billing-information--side">
                <common-content-spacer height="micro">
                    <p>{{ $t('pages.practice.billing.plan_type') }}</p>
                    <div class="billing-information--side--plan">
                        <common-page-header
                            :heading="getBillingSubscriptionName"
                            class="referral-details__heading"
                            size="large"
                        ></common-page-header>
                        <div>
                            <p v-if="isAnnual" class="billing-form--info">
                                <span>${{ yearlyPrice }}</span>
                                {{ $t('pages.billing.billing.billing_form.year') }}
                            </p>
                            <p v-else class="billing-form--info">
                                <span>${{ monthlyPrice }}</span>
                                {{ $t('pages.billing.billing.billing_form.month') }}
                            </p>
                        </div>
                    </div>
                    <div class="billing-information--side--price__save">
                        <div class="billing-information--side--price__save--badge">
                            <common-icon
                                name="member-transparent"
                                size="small"
                                class="billing-information--side--price__save--icon"
                            ></common-icon>

                            <div class="billing-information--side--price__save--content">
                                <span class="billing-information--side--price__save--label">
                                    {{ $t('pages.practice.billing.you_save') }}
                                </span>

                                <span class="billing-information--side--price__save--amount">
                                    ${{ 12 * monthlyPrice - yearlyPrice }}
                                </span>

                                <span class="billing-information--side--price__save--annual">
                                    {{ $t('pages.billing.billing.on_annual') }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="billing-information--side--price">
                        <div class="billing-form-type">
                            <span>{{ $t('pages.billing.billing.billing_form.monthly') }}</span>
                            <switch-input
                                :value="isAnnual"
                                spacing="space-between"
                                :showBlueCircle="true"
                                @change="changeIsAnnual($event)"
                            ></switch-input>
                            <span>{{ $t('pages.billing.billing.billing_form.annual') }}</span>
                        </div>
                    </div>
                    <common-tabs :tabs="tabs" :activeTab="tabs[0].name">
                        <template v-slot:features>
                            <billing-features-list :featuresList="featuresList" :isHeaderCenter="false"></billing-features-list>
                        </template>
                        <template v-slot:details>
                            <price-explaination v-if="numberOfItems"></price-explaination>
                        </template>
                    </common-tabs>
                </common-content-spacer>
            </div>
            <div class="billing-information--main">
                <div v-if="shouldShowCardList" class="cards-container">
                    <common-page-header
                        heading="Saved payment methods"
                        class="referral-details__heading"
                        contentSpacing="medium"
                        size="page-plain-header"
                    ></common-page-header>
                    <div class="saved-cards__carousel">
                        <div
                            v-for="card in cardList"
                            :key="card.id"
                            :class="getCardItemClass(card)"
                            @click="selectedCardId = card.id"
                        >
                            <div class="saved-cards__content">
                                <span class="saved-cards__brand">
                                    {{ getCardBrandName(card.card.brand) }}
                                </span>

                                <span class="saved-cards__number">•••• {{ card.card.last4 }}</span>
                            </div>

                            <div :class="getIndicatorClass(card)">
                                <common-icon v-if="selectedCardId === card.id" name="white-check" size="small"></common-icon>
                            </div>
                        </div>
                    </div>
                    <common-buttons align="center">
                        <common-button
                            :isProcessing="isCardPaymentLoading"
                            :label="$t('common.microcopy.continue')"
                            type="primary"
                            size="small"
                            @click="triggerSavedCardSubscription"
                        ></common-button>
                    </common-buttons>
                    <common-divider>
                        <p>{{ $t('pages.auth.signup.steps.register.description.or') }}</p>
                    </common-divider>
                    <p class="saved-cards__payment-heading" @click="showNewCardFrom = true">
                        <common-icon name="referral-add" size="small"></common-icon>
                        Add a new payment method
                    </p>
                </div>
                <billing-payment-form
                    v-if="shouldShowPaymentForm"
                    :monthlyPrice="monthlyPrice"
                    :yearlyPrice="yearlyPrice"
                    :isAlreadySubscribed="isAlreadySubscribed"
                    :isAnnual="isAnnual"
                    @billing-address-saved="saveBillingAddress"
                    @subscription-created="subscriptionCreated"
                ></billing-payment-form>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import BillingFeaturesList from 'components/page-elements/practice/billing/BillingFeaturesList'
    import PricingCards from 'components/page-elements/practice/billing/PricingCards'
    import BillingPaymentForm from 'components/page-elements/practice/billing/BillingPaymentForm'
    import PracticesAPI from 'api/PracticesAPI'
    import SwitchInput from 'components/form-inputs/SwitchInput'
    import PriceExplaination from 'components/page-elements/practice/billing/PriceExplaination'

    export default {
        mixins: [FormMixin],
        components: { BillingFeaturesList, BillingPaymentForm, SwitchInput, PriceExplaination, PricingCards },

        props: {
            isAlreadySubscribed: { type: Boolean, default: false },
            mode: { type: String, default: 'subscription' },
        },

        data() {
            return {
                isSending: false,
                featuresList: [],
                monthlyPrice: null,
                yearlyPrice: null,
                isAnnual: true,
                numberOfItems: null,
                showPricingDetails: true,
                cardList: [],
                selectedCardId: null,
                showNewCardFrom: false,
                isCardPaymentLoading: false,
            }
        },

        computed: {
            tabs() {
                return [
                    {
                        name: 'features',
                        displayName: this.$t('pages.billing.billing.features'),
                    },
                    {
                        name: 'details',
                        displayName: this.$t('pages.billing.billing.billing_details'),
                    },
                ]
            },

            getBillingSubscriptionName() {
                return this.$auth.isPracticeAncillary()
                    ? this.$t('pages.billing.billing.enterprise')
                    : this.$t('pages.billing.billing.premium')
            },
            shouldShowCardList() {
                if (this.mode === 'add-card') {
                    return false
                }

                return this.cardList.length > 0
            },

            shouldShowPaymentForm() {
                if (this.mode === 'add-card') {
                    return true
                }

                return this.cardList.length === 0 || this.showNewCardFrom
            },
            getIndicatorClass() {
                return (card) => ({
                    'saved-cards__indicator': true,
                    'saved-cards__indicator--selected': this.selectedCardId === card.id,
                })
            },
            getCardItemClass() {
                return (card) => ({
                    'saved-cards__item': true,
                    'saved-cards__item--selected': this.selectedCardId === card.id,
                })
            },
            isPracticePremium() {
                return this.$auth.isPracticePremium()
            },
        },

        methods: {
            saveBillingAddress() {
                this.emitEvent({ name: 'close' })
                this.$auth.forceUpdatePractice()
            },

            getEstimatedPrice() {
                PracticesAPI.getEstimatedPrice().then((response) => {
                    this.monthlyPrice = response.monthlyPrice
                    this.yearlyPrice = response.yearlyPrice
                    this.numberOfItems = response.numberOfItems
                })
            },

            changeIsAnnual() {
                this.isAnnual = !this.isAnnual
            },

            subscriptionCreated() {
                this.emitEvent({ name: 'subscription-created' })
                this.$auth.forceUpdatePractice()
                this.emitEvent({ name: 'close' })
            },
            getPracticeCards() {
                PracticesAPI.getPracticeCards().then((response) => {
                    this.cardList = response
                })
            },
            getCardBrandName(brand) {
                if (brand === 'mastercard') return 'mc'.toUpperCase()

                return brand.toUpperCase()
            },
            triggerSavedCardSubscription() {
                this.isCardPaymentLoading = true
                PracticesAPI.createSubscription({
                    paymentMethodId: this.selectedCardId,
                    isAnnual: this.isAnnual,
                    billingAddress: this.$auth.getPractice().billingAddress,
                }).then(() => {
                    this.isCardPaymentLoading = false
                    this.saveBillingAddress()
                    this.subscriptionCreated()
                })
            },
        },

        mounted() {
            this.getEstimatedPrice()
            this.featuresList = [
                this.$t('pages.practice.billing.payment_form_list.item_1'),
                this.$t('pages.practice.billing.payment_form_list.item_2'),
                this.$t('pages.practice.billing.payment_form_list.item_3'),
                this.$t('pages.practice.billing.payment_form_list.item_4'),
                this.$t('pages.practice.billing.payment_form_list.item_5'),
                this.$t('pages.practice.billing.payment_form_list.item_6'),
                this.$t('pages.practice.billing.payment_form_list.item_7'),
                this.$t('pages.practice.billing.payment_form_list.item_8'),
                this.$t('pages.practice.billing.payment_form_list.item_9'),
                this.$t('pages.practice.billing.payment_form_list.item_10'),
            ]
            if (this.mode === 'subscription') {
                this.getPracticeCards()
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .billing-default-plan {
        &--info {
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 700;
            font-size: 18px;
            line-height: 28px;
            color: $color--txt-base-5;
            text-align: center;
        }

        &--accept {
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 500;
            font-size: 14px;
            line-height: 28px;
            color: $color--txt-base-5;
            text-align: center;

            .icon {
                margin-left: 15px;
            }
        }
    }
    .billing-form-type {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 12px;
        max-width: 260px;
        .form-field {
            margin: 0 10px;
        }
    }
    .billing-information {
        display: flex;

        @include breakpoint(tablet) {
            display: block;
            max-height: 700px;
        }

        &--side {
            @include breakpoint(tablet) {
                min-width: 100%;
                padding-right: 0;
            }
            background: $color--bg-dashboard;
            border-radius: 8px;
            padding: 10px;
            min-width: 371px;

            .tab-wrapper {
                .tab {
                    @include breakpoint(tablet) {
                        width: 100%;
                    }
                }
            }

            &--plan {
                display: flex;
                justify-content: space-between;
                align-items: center;
                p {
                    font-weight: 600;
                    color: $color-black;
                }
            }

            &--price {
                &__value {
                    font-weight: 600;
                    font-size: 20px;
                    line-height: 30px;
                    letter-spacing: 0.325px;
                    color: $color--accent-3;
                }

                &__save {
                    display: flex;
                    justify-content: flex-end;
                    margin-bottom: 20px;

                    &--badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;

                        padding: 4px 12px;

                        border-radius: 999px;

                        background: linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%);

                        border: 1px solid #a7f3d0;

                        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.12), 0 2px 6px rgba(16, 185, 129, 0.08);
                    }

                    &--icon {
                        color: #059669;
                        flex-shrink: 0;
                    }

                    &--content {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        flex-wrap: wrap;
                    }

                    &--label {
                        font-size: 12px;
                        font-weight: 500;
                        color: #065f46;
                    }

                    &--amount {
                        font-size: 15px;
                        font-weight: 700;
                        color: #059669;
                    }

                    &--annual {
                        font-size: 12px;
                        font-weight: 600;
                        color: #065f46;
                    }
                }
            }
        }

        &--main {
            @include breakpoint(tablet) {
                padding: 0;
            }

            width: 100%;
            max-height: 100vh;
            overflow-x: hidden;
        }
        .cards-container {
            padding: 10px;
        }
        .saved-cards {
            margin-top: 20px;

            &__carousel {
                display: flex;
                gap: 14px;
                margin-bottom: 20px;
                overflow-x: auto;
                overflow-y: hidden;

                padding: 8px 2px 14px;

                scroll-behavior: smooth;

                scrollbar-width: thin;
                scrollbar-color: $color--accent-1 transparent;

                &::-webkit-scrollbar {
                    height: 7px;
                }

                &::-webkit-scrollbar-track {
                    background: #eef3f7;
                    border-radius: 20px;
                }

                &::-webkit-scrollbar-thumb {
                    background: $color--accent-1;
                    border-radius: 20px;
                }

                &::-webkit-scrollbar-thumb:hover {
                    background: darken($color--accent-1, 8%);
                }
            }

            &__item {
                flex: 0 0 auto;

                min-width: 235px;
                max-width: 235px;

                height: 64px;

                display: flex;
                align-items: center;
                justify-content: space-between;

                padding: 0 18px;

                border-radius: 999px;

                border: 2px solid #e8edf2;

                background: #fff;

                cursor: pointer;

                transition: 0.25s ease;

                &:hover {
                    transform: translateY(-2px);
                    border-color: $color--accent-1;
                    box-shadow: 0 8px 22px rgba(19, 179, 227, 0.12);
                }

                &--selected {
                    border-color: $color--accent-1;
                    background: linear-gradient(90deg, rgba(19, 179, 227, 0.06), rgba(19, 179, 227, 0.01));

                    box-shadow: 0 10px 24px rgba(19, 179, 227, 0.15);
                }
            }

            &__content {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            &__brand {
                color: #1557c0;
                font-size: 16px;
                font-weight: 700;
            }

            &__number {
                font-size: 15px;
                color: $color--txt-base-5;
            }

            &__indicator {
                width: 28px;
                height: 28px;

                border-radius: 50%;

                border: 2px solid #d9e2ec;

                display: flex;
                align-items: center;
                justify-content: center;

                transition: 0.2s ease;

                &--selected {
                    background: $color--accent-1;
                    border-color: $color--accent-1;
                }
            }
            &__payment-heading {
                color: $color--accent-1;
                cursor: pointer;
                font-weight: 500;
                .icon {
                    margin-right: 6px;
                }
            }
        }
    }
</style>
